import React, { useState } from 'react';
import styled, { css } from 'styled-components';

// ---- Styled Components ----
const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  background: #f8f9fa;
  width: 100%;
  font-family: 'Raleway',sans-serif;
`;

const SectionTitle = styled.h2`
  margin: 0px;
  font-size: 1.5rem;
  color: #333;
`;

const ToneOptionsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const ToneCard = styled.div`
  background: ${props => (props.selected ? '#3b82f6' : '#fff')};
  color: ${props => (props.selected ? '#fff' : '#333')};
  border-radius: 8px;
  border: 2px solid ${props => (props.selected ? '#3b82f6' : '#e0e0e0')};
  padding: 16px;
  cursor: pointer;
  transition: border-color 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &:hover {
    border-color: #a5b4fc;
  }

  .icon {
    width: 24px;
    height: 24px;
    margin-bottom: 8px;
  }

  h3 {
    margin-bottom: 5px;
    margin-top:20px;
    font-size: 1.2rem;
  }

  p {
    margin: 0;
    line-height: 1.4;
    margin-bottom: 15px;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
`;

const ToggleLabel = styled.label`
  margin-left: 8px;
  font-size: 1rem;
  color: #333;
`;

const Switch = styled.input.attrs({ type: 'checkbox' })`
  appearance: none;
  width: 40px;
  height: 22px;
  background: #d1d5db;
  border-radius: 11px;
  position: relative;
  cursor: pointer;
  outline: none;
  transition: background 0.3s ease;

  &:checked {
    background: #3b82f6;
  }

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    background: #fff;
    border-radius: 50%;
    transition: transform 0.3s ease;
  }

  &:checked::before {
    transform: translateX(18px);
  }
`;

const AgentOptionsContainer = styled.div`
  max-height: ${props => (props.visible ? '1000px' : '0px')};
  overflow: hidden;
  opacity: ${props => (props.visible ? 1 : 0)};
  transition: all 0.4s ease;
  margin-bottom: ${props => (props.visible ? '16px' : '0')};
`;

const AgentOptionRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;

  label {
    font-weight: 600;
    margin-bottom: 4px;
    color: #333;
  }

  input[type='number'],
  input[type='text'],
  textarea {
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 0.9rem;
  }
`;

const MemoryToggleRow = styled.div`
  display: flex;
  align-items: center;

  label {
    margin-left: 8px;
  }
`;

const P = styled.p`
  margin-top:10px;
  max-width: 700px;
`

const Title = styled.h3`
  margin-top:20px;
`

// ---- Icon Components ----
const EmpatheticIcon = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 7.8C3 6.11984 3 5.27976 3.32698 4.63803C3.6146 4.07354 4.07354 3.6146 4.63803 3.32698C5.27976 3 6.11984 3 7.8 3H16.2C17.8802 3 18.7202 3 19.362 3.32698C19.9265 3.6146 20.3854 4.07354 20.673 4.63803C21 5.27976 21 6.11984 21 7.8V13.5C21 14.8978 21 15.5967 20.7716 16.1481C20.4672 16.8831 19.8831 17.4672 19.1481 17.7716C18.5967 18 17.8978 18 16.5 18C16.0114 18 15.7671 18 15.5405 18.0535C15.2383 18.1248 14.9569 18.2656 14.7185 18.4645C14.5397 18.6137 14.3931 18.8091 14.1 19.2L12.64 21.1467C12.4229 21.4362 12.3143 21.5809 12.1812 21.6327C12.0647 21.678 11.9353 21.678 11.8188 21.6327C11.6857 21.5809 11.5771 21.4362 11.36 21.1467L9.9 19.2C9.60685 18.8091 9.46028 18.6137 9.2815 18.4645C9.04312 18.2656 8.76169 18.1248 8.45951 18.0535C8.23287 18 7.98858 18 7.5 18C6.10218 18 5.40326 18 4.85195 17.7716C4.11687 17.4672 3.53284 16.8831 3.22836 16.1481C3 15.5967 3 14.8978 3 13.5V7.8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M11.9973 8.33059C11.1975 7.4216 9.8639 7.17708 8.86188 8.00945C7.85986 8.84182 7.71879 10.2335 8.50568 11.2179C8.97361 11.8033 10.1197 12.8531 10.9719 13.6079C11.3237 13.9195 11.4996 14.0753 11.7114 14.1385C11.8925 14.1926 12.102 14.1926 12.2832 14.1385C12.4949 14.0753 12.6708 13.9195 13.0226 13.6079C13.8748 12.8531 15.0209 11.8033 15.4888 11.2179C16.2757 10.2335 16.1519 8.83306 15.1326 8.00945C14.1134 7.18584 12.797 7.4216 11.9973 8.33059Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ActionIcon = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 14.9998L9 11.9998M12 14.9998C13.3968 14.4685 14.7369 13.7985 16 12.9998M12 14.9998V19.9998C12 19.9998 15.03 19.4498 16 17.9998C17.08 16.3798 16 12.9998 16 12.9998M9 11.9998C9.53214 10.6192 10.2022 9.29582 11 8.04976C12.1652 6.18675 13.7876 4.65281 15.713 3.59385C17.6384 2.53489 19.8027 1.98613 22 1.99976C22 4.71976 21.22 9.49976 16 12.9998M9 11.9998H4C4 11.9998 4.55 8.96976 6 7.99976C7.62 6.91976 11 7.99976 11 7.99976M4.5 16.4998C3 17.7598 2.5 21.4998 2.5 21.4998C2.5 21.4998 6.24 20.9998 7.5 19.4998C8.21 18.6598 8.2 17.3698 7.41 16.5898C7.02131 16.2188 6.50929 16.0044 5.97223 15.9878C5.43516 15.9712 4.91088 16.1535 4.5 16.4998Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DeepIcon = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 4H7.8C6.11984 4 5.27976 4 4.63803 4.32698C4.07354 4.6146 3.6146 5.07354 3.32698 5.63803C3 6.27976 3 7.11984 3 8.8V14C3 14.93 3 15.395 3.10222 15.7765C3.37962 16.8117 4.18827 17.6204 5.22354 17.8978C5.60504 18 6.07003 18 7 18V20.3355C7 20.8684 7 21.1348 7.10923 21.2716C7.20422 21.3906 7.34827 21.4599 7.50054 21.4597C7.67563 21.4595 7.88367 21.2931 8.29976 20.9602L10.6852 19.0518C11.1725 18.662 11.4162 18.4671 11.6875 18.3285C11.9282 18.2055 12.1844 18.1156 12.4492 18.0613C12.7477 18 13.0597 18 13.6837 18H15.2C16.8802 18 17.7202 18 18.362 17.673C18.9265 17.3854 19.3854 16.9265 19.673 16.362C20 15.7202 20 14.8802 20 13.2V13M20.1213 3.87868C21.2929 5.05025 21.2929 6.94975 20.1213 8.12132C18.9497 9.29289 17.0503 9.29289 15.8787 8.12132C14.7071 6.94975 14.7071 5.05025 15.8787 3.87868C17.0503 2.70711 18.9497 2.70711 20.1213 3.87868Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BalancedIcon = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.50047 13H8.50047M15.5005 13H21.5005M12.0005 7V21M12.0005 7C13.3812 7 14.5005 5.88071 14.5005 4.5M12.0005 7C10.6198 7 9.50047 5.88071 9.50047 4.5M4.00047 21L20.0005 21M4.00047 4.50001L9.50047 4.5M9.50047 4.5C9.50047 3.11929 10.6198 2 12.0005 2C13.3812 2 14.5005 3.11929 14.5005 4.5M14.5005 4.5L20.0005 4.5M8.88091 14.3364C8.48022 15.8706 7.11858 17 5.50047 17C3.88237 17 2.52073 15.8706 2.12004 14.3364C2.0873 14.211 2.07093 14.1483 2.06935 13.8979C2.06838 13.7443 2.12544 13.3904 2.17459 13.2449C2.25478 13.0076 2.34158 12.8737 2.51519 12.6059L5.50047 8L8.48576 12.6059C8.65937 12.8737 8.74617 13.0076 8.82636 13.2449C8.87551 13.3904 8.93257 13.7443 8.9316 13.8979C8.93002 14.1483 8.91365 14.211 8.88091 14.3364ZM21.8809 14.3364C21.4802 15.8706 20.1186 17 18.5005 17C16.8824 17 15.5207 15.8706 15.12 14.3364C15.0873 14.211 15.0709 14.1483 15.0693 13.8979C15.0684 13.7443 15.1254 13.3904 15.1746 13.2449C15.2548 13.0076 15.3416 12.8737 15.5152 12.6059L18.5005 8L21.4858 12.6059C21.6594 12.8737 21.7462 13.0076 21.8264 13.2449C21.8755 13.3904 21.9326 13.7443 21.9316 13.8979C21.93 14.1483 21.9137 14.211 21.8809 14.3364Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ---- Main Com// ---- Main Component ----
export default function AISettings({ aiOptions, setAIOptions }) {
    const {
      selectedTone,
      agentActive,
      maxQuestions,
      agentGoal,
      agentContext,
      memoryEnabled,
    } = aiOptions;
  
    const updateOption = (optionName, value) => {
      setAIOptions(prev => ({ ...prev, [optionName]: value }));
    };
  
    return (
      <TabContainer>
        <SectionTitle>AI Options</SectionTitle>
        <P>Customize the AI survey experience with a few simple clicks.</P>
        <SectionTitle>Conversational Style</SectionTitle>
        <P>
          Select the tone and behavior of the AI agent to tailor the conversation to your needs.
          Changing the approach will alter the style and techniques our AI uses when interacting with respondents.
        </P>
  
        {/* 1. Tone Selection */}
        <ToneOptionsContainer>
          <ToneCard
            selected={selectedTone === 'balanced'}
            onClick={() => updateOption('selectedTone', 'balanced')}
          >
            <BalancedIcon />
            <Title>Balanced (default)</Title>
            <p>Offers a well-rounded approach that combines empathy with action.</p>
            <P>
              <strong>When to use:</strong> When a mixture of understanding and decisiveness is required.
            </P>
          </ToneCard>
  
          <ToneCard
            selected={selectedTone === 'empathetic'}
            onClick={() => updateOption('selectedTone', 'empathetic')}
          >
            <EmpatheticIcon />
            <Title>Empathetic Researcher</Title>
            <p>Focus on understanding emotional nuances and underlying needs.</p>
            <P>
              <strong>When to use:</strong> When you need to explore user feelings and capture the human element.
            </P>
          </ToneCard>
  
          <ToneCard
            selected={selectedTone === 'action'}
            onClick={() => updateOption('selectedTone', 'action')}
          >
            <ActionIcon />
            <Title>Action Oriented</Title>
            <p>Prioritizes quick solutions and decisive action.</p>
            <P>
              <strong>When to use:</strong> When immediate, results-driven insights are needed.
            </P>
          </ToneCard>
  
          <ToneCard
            selected={selectedTone === 'root'}
            onClick={() => updateOption('selectedTone', 'root')}
          >
            <DeepIcon />
            <Title>Deep Root Cause Investigation</Title>
            <p>Dives deep into data and context to uncover fundamental issues.</p>
            <P>
              <strong>When to use:</strong> When solving complex problems requires a thorough analysis.
            </P>
          </ToneCard>
        </ToneOptionsContainer>
  
        {/* 2. AI Agent Toggle */}
        <ToggleContainer>
          <Switch
            checked={agentActive}
            onChange={() => updateOption('agentActive', !agentActive)}
          />
          <ToggleLabel>Enable AI Agent</ToggleLabel>
        </ToggleContainer>
        <p style={{ marginTop: '-8px', marginBottom: '24px', color: '#666' }}>
          When enabled, the AI agent can dynamically generate questions to gather information based on a specific goal.
        </p>
  
        {/* Additional Agent Options (appear when toggle is active) */}
        <AgentOptionsContainer visible={agentActive}>
          <AgentOptionRow>
            <label htmlFor="max-questions">
              Maximum number of questions (limit 20)
            </label>
            <input
              id="max-questions"
              type="number"
              min="1"
              max="20"
              value={maxQuestions}
              onChange={(e) => updateOption('maxQuestions', Number(e.target.value))}
            />
          </AgentOptionRow>
  
          <AgentOptionRow>
            <label htmlFor="agent-goal">
              The goal of the agent (what is it trying to determine?)
            </label>
            <input
              id="agent-goal"
              type="text"
              placeholder="e.g. Diagnose user’s biggest challenge"
              value={agentGoal}
              onChange={(e) => updateOption('agentGoal', e.target.value)}
            />
          </AgentOptionRow>
  
          <AgentOptionRow>
            <label htmlFor="agent-context">
              Agent context (what information is meaningful?)
            </label>
            <textarea
              id="agent-context"
              placeholder="e.g. The user’s work situation, personal concerns, resource constraints..."
              rows={3}
              value={agentContext}
              onChange={(e) => updateOption('agentContext', e.target.value)}
            />
          </AgentOptionRow>
  
          <MemoryToggleRow>
            <Switch
              checked={memoryEnabled}
              onChange={() => updateOption('memoryEnabled', !memoryEnabled)}
            />
            <label>Enable Memory (consider past user interactions)</label>
          </MemoryToggleRow>
        </AgentOptionsContainer>
      </TabContainer>
    );
  }